/**
 * DrawingPreview - Renders preview geometry for drawing tools
 *
 * Shows live preview of circles, rectangles, polygons, etc.
 * while the user is defining the second point.
 */

import React, { useMemo } from 'react';
import * as THREE from 'three';
import { Line } from '@react-three/drei';
import { GEOMETRY_COLORS } from '../utils/constants';

const Z_PREVIEW = 10; // Z-level for preview (above other geometry)
const PREVIEW_COLOR = '#00ffff'; // Cyan for preview
const PREVIEW_OPACITY = 0.7;

/**
 * Circle preview (2D outline)
 */
function CirclePreview({ center, radius }) {
  const points = useMemo(() => {
    const segments = 64;
    const pts = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      pts.push(new THREE.Vector3(
        center.x + radius * Math.cos(angle),
        center.y + radius * Math.sin(angle),
        Z_PREVIEW
      ));
    }
    return pts;
  }, [center, radius]);

  if (radius <= 0) return null;

  return (
    <Line
      points={points}
      color={PREVIEW_COLOR}
      lineWidth={2}
      dashed={true}
      dashSize={200}
      gapSize={100}
    />
  );
}

/**
 * Rectangle preview (2D outline)
 */
function RectanglePreview({ corner1, corner2 }) {
  const points = useMemo(() => [
    new THREE.Vector3(corner1.x, corner1.y, Z_PREVIEW),
    new THREE.Vector3(corner2.x, corner1.y, Z_PREVIEW),
    new THREE.Vector3(corner2.x, corner2.y, Z_PREVIEW),
    new THREE.Vector3(corner1.x, corner2.y, Z_PREVIEW),
    new THREE.Vector3(corner1.x, corner1.y, Z_PREVIEW), // Close the rectangle
  ], [corner1, corner2]);

  return (
    <Line
      points={points}
      color={PREVIEW_COLOR}
      lineWidth={2}
      dashed={true}
      dashSize={200}
      gapSize={100}
    />
  );
}

/**
 * Polygon preview (2D outline)
 */
function PolygonPreview({ center, radius, sides = 6, rotation = 0 }) {
  const points = useMemo(() => {
    const pts = [];
    for (let i = 0; i <= sides; i++) {
      const angle = rotation + (i / sides) * Math.PI * 2;
      pts.push(new THREE.Vector3(
        center.x + radius * Math.cos(angle),
        center.y + radius * Math.sin(angle),
        Z_PREVIEW
      ));
    }
    return pts;
  }, [center, radius, sides, rotation]);

  if (radius <= 0) return null;

  return (
    <Line
      points={points}
      color={PREVIEW_COLOR}
      lineWidth={2}
      dashed={true}
      dashSize={200}
      gapSize={100}
    />
  );
}

/**
 * Line preview
 */
function LinePreview({ start, end }) {
  const points = useMemo(() => [
    new THREE.Vector3(start.x, start.y, Z_PREVIEW),
    new THREE.Vector3(end.x, end.y, Z_PREVIEW),
  ], [start, end]);

  return (
    <Line
      points={points}
      color={PREVIEW_COLOR}
      lineWidth={2}
      dashed={true}
      dashSize={200}
      gapSize={100}
    />
  );
}

/**
 * Construction line preview (extends to "infinity")
 */
function ConstructionLinePreview({ point1, point2 }) {
  const points = useMemo(() => {
    const dx = point2.x - point1.x;
    const dy = point2.y - point1.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = dx / len;
    const ny = dy / len;
    const EXTEND = 50000; // 50m extension

    return [
      new THREE.Vector3(point1.x - nx * EXTEND, point1.y - ny * EXTEND, Z_PREVIEW),
      new THREE.Vector3(point1.x + nx * EXTEND, point1.y + ny * EXTEND, Z_PREVIEW),
    ];
  }, [point1, point2]);

  return (
    <Line
      points={points}
      color="#808080" // Gray for construction
      lineWidth={1}
      dashed={true}
      dashSize={300}
      gapSize={150}
    />
  );
}

/**
 * Cylinder preview (2D circle + vertical lines)
 */
function CylinderPreview({ center, radius, height }) {
  const basePoints = useMemo(() => {
    const segments = 64;
    const pts = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      pts.push(new THREE.Vector3(
        center.x + radius * Math.cos(angle),
        center.y + radius * Math.sin(angle),
        Z_PREVIEW
      ));
    }
    return pts;
  }, [center, radius]);

  // Top circle (3D preview)
  const topPoints = useMemo(() => {
    const segments = 32;
    const pts = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      pts.push(new THREE.Vector3(
        center.x + radius * Math.cos(angle),
        center.y + radius * Math.sin(angle),
        height
      ));
    }
    return pts;
  }, [center, radius, height]);

  // Vertical lines at cardinal points
  const verticalLines = useMemo(() => {
    const lines = [];
    for (let i = 0; i < 4; i++) {
      const angle = (i / 4) * Math.PI * 2;
      const x = center.x + radius * Math.cos(angle);
      const y = center.y + radius * Math.sin(angle);
      lines.push([
        new THREE.Vector3(x, y, 0),
        new THREE.Vector3(x, y, height),
      ]);
    }
    return lines;
  }, [center, radius, height]);

  if (radius <= 0) return null;

  return (
    <group>
      {/* Base circle */}
      <Line points={basePoints} color={PREVIEW_COLOR} lineWidth={2} dashed dashSize={200} gapSize={100} />
      {/* Top circle */}
      <Line points={topPoints} color={PREVIEW_COLOR} lineWidth={1} dashed dashSize={200} gapSize={100} />
      {/* Vertical lines */}
      {verticalLines.map((pts, i) => (
        <Line key={i} points={pts} color={PREVIEW_COLOR} lineWidth={1} dashed dashSize={200} gapSize={100} />
      ))}
    </group>
  );
}

/**
 * Sphere preview (2D circle + vertical arc)
 */
function SpherePreview({ center, radius }) {
  // Horizontal circle at center height
  const hCirclePoints = useMemo(() => {
    const segments = 64;
    const pts = [];
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      pts.push(new THREE.Vector3(
        center.x + radius * Math.cos(angle),
        center.y + radius * Math.sin(angle),
        center.z || radius
      ));
    }
    return pts;
  }, [center, radius]);

  // Vertical arc (XZ plane)
  const vArcPoints = useMemo(() => {
    const segments = 32;
    const pts = [];
    const z = center.z || radius;
    for (let i = 0; i <= segments; i++) {
      const angle = (i / segments) * Math.PI * 2;
      pts.push(new THREE.Vector3(
        center.x + radius * Math.cos(angle),
        center.y,
        z + radius * Math.sin(angle)
      ));
    }
    return pts;
  }, [center, radius]);

  if (radius <= 0) return null;

  return (
    <group>
      <Line points={hCirclePoints} color={PREVIEW_COLOR} lineWidth={2} dashed dashSize={200} gapSize={100} />
      <Line points={vArcPoints} color={PREVIEW_COLOR} lineWidth={1} dashed dashSize={200} gapSize={100} />
    </group>
  );
}

/**
 * Box preview (3D wireframe)
 */
function BoxPreview({ corner1, corner2, height }) {
  const edges = useMemo(() => {
    const x1 = Math.min(corner1.x, corner2.x);
    const x2 = Math.max(corner1.x, corner2.x);
    const y1 = Math.min(corner1.y, corner2.y);
    const y2 = Math.max(corner1.y, corner2.y);
    const z1 = 0;
    const z2 = height;

    // 12 edges of a box
    return [
      // Bottom face
      [[x1, y1, z1], [x2, y1, z1]],
      [[x2, y1, z1], [x2, y2, z1]],
      [[x2, y2, z1], [x1, y2, z1]],
      [[x1, y2, z1], [x1, y1, z1]],
      // Top face
      [[x1, y1, z2], [x2, y1, z2]],
      [[x2, y1, z2], [x2, y2, z2]],
      [[x2, y2, z2], [x1, y2, z2]],
      [[x1, y2, z2], [x1, y1, z2]],
      // Vertical edges
      [[x1, y1, z1], [x1, y1, z2]],
      [[x2, y1, z1], [x2, y1, z2]],
      [[x2, y2, z1], [x2, y2, z2]],
      [[x1, y2, z1], [x1, y2, z2]],
    ].map(edge => edge.map(p => new THREE.Vector3(p[0], p[1], p[2])));
  }, [corner1, corner2, height]);

  return (
    <group>
      {edges.map((pts, i) => (
        <Line key={i} points={pts} color={PREVIEW_COLOR} lineWidth={1} dashed dashSize={200} gapSize={100} />
      ))}
    </group>
  );
}

/**
 * Dimension annotation showing current measurement
 */
function DimensionAnnotation({ firstPoint, currentPoint, dimension, angle }) {
  const midPoint = useMemo(() => ({
    x: (firstPoint.x + currentPoint.x) / 2,
    y: (firstPoint.y + currentPoint.y) / 2,
  }), [firstPoint, currentPoint]);

  // Format dimension for display
  const text = useMemo(() => {
    if (typeof dimension === 'object') {
      return `${(dimension.width / 1000).toFixed(2)} × ${((dimension.height || dimension.depth) / 1000).toFixed(2)} m`;
    }
    return `${(dimension / 1000).toFixed(2)} m`;
  }, [dimension]);

  // Offset perpendicular to line
  const offsetDist = 300; // 30cm offset
  const perpAngle = angle + Math.PI / 2;
  const labelPos = {
    x: midPoint.x + offsetDist * Math.cos(perpAngle),
    y: midPoint.y + offsetDist * Math.sin(perpAngle),
  };

  return (
    <group position={[labelPos.x, labelPos.y, Z_PREVIEW + 1]}>
      {/* Background for readability */}
      <mesh>
        <planeGeometry args={[text.length * 80 + 100, 200]} />
        <meshBasicMaterial color="#000000" transparent opacity={0.7} />
      </mesh>
    </group>
  );
}

/**
 * First point marker
 */
function FirstPointMarker({ point }) {
  return (
    <group position={[point.x, point.y, Z_PREVIEW]}>
      {/* Cross marker */}
      <Line
        points={[
          new THREE.Vector3(-150, 0, 0),
          new THREE.Vector3(150, 0, 0),
        ]}
        color={PREVIEW_COLOR}
        lineWidth={2}
      />
      <Line
        points={[
          new THREE.Vector3(0, -150, 0),
          new THREE.Vector3(0, 150, 0),
        ]}
        color={PREVIEW_COLOR}
        lineWidth={2}
      />
      {/* Center dot */}
      <mesh>
        <circleGeometry args={[50, 16]} />
        <meshBasicMaterial color={PREVIEW_COLOR} />
      </mesh>
    </group>
  );
}

/**
 * Radius line from center to current point
 */
function RadiusLine({ from, to }) {
  const points = useMemo(() => [
    new THREE.Vector3(from.x, from.y, Z_PREVIEW),
    new THREE.Vector3(to.x, to.y, Z_PREVIEW),
  ], [from, to]);

  return (
    <Line
      points={points}
      color="#ffff00" // Yellow for radius
      lineWidth={1}
      dashed={true}
      dashSize={100}
      gapSize={50}
    />
  );
}

/**
 * Main Drawing Preview Component
 */
export function DrawingPreview({
  preview,
  firstPoint,
  currentPoint,
  dimension,
  angle,
}) {
  if (!preview) return null;

  return (
    <group name="drawingPreview">
      {/* First point marker */}
      {firstPoint && <FirstPointMarker point={firstPoint} />}

      {/* Radius/dimension line */}
      {firstPoint && currentPoint && (
        <RadiusLine from={firstPoint} to={currentPoint} />
      )}

      {/* Preview geometry by type */}
      {preview.type === 'circle' && (
        <CirclePreview center={preview.center} radius={preview.radius} />
      )}

      {preview.type === 'rectangle' && (
        <RectanglePreview corner1={preview.corner1} corner2={preview.corner2} />
      )}

      {preview.type === 'polygon' && (
        <PolygonPreview
          center={preview.center}
          radius={preview.radius}
          sides={preview.sides}
          rotation={preview.rotation}
        />
      )}

      {preview.type === 'line' && (
        <LinePreview start={preview.start} end={preview.end} />
      )}

      {preview.type === 'construction_line' && (
        <ConstructionLinePreview point1={preview.point1} point2={preview.point2} />
      )}

      {preview.type === 'cylinder' && (
        <CylinderPreview
          center={preview.center}
          radius={preview.radius}
          height={preview.height}
        />
      )}

      {preview.type === 'sphere' && (
        <SpherePreview center={preview.center} radius={preview.radius} />
      )}

      {preview.type === 'box' && (
        <BoxPreview
          corner1={preview.corner1}
          corner2={preview.corner2}
          height={preview.height}
        />
      )}
    </group>
  );
}

export default DrawingPreview;
